import { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Stack } from '@rossum/ui/material';
import {
  FEATURE_ICON_GAP,
  FEATURE_ICON_SIZE,
  MAX_VISIBLE_CHANGES,
  ROW_SPACING,
} from '../constants';
import { countValueChanges, sliceValueChanges } from '../helpers/valueChanges';
import type { ActivityBlock as ActivityBlockModel } from '../model';
import { ActivityBlockHeader } from './ActivityBlockHeader';

type ActivityBlockProps = {
  block: ActivityBlockModel;
};

export const ActivityBlock = ({ block }: ActivityBlockProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const totalChanges = countValueChanges(block.rows);
  const visibleRows = isExpanded
    ? block.rows
    : sliceValueChanges(block.rows, MAX_VISIBLE_CHANGES);
  const hasHiddenChanges = totalChanges > MAX_VISIBLE_CHANGES;

  return (
    <Stack spacing={1.5} sx={{ py: 2 }} data-cy="document-history-block">
      <ActivityBlockHeader block={block} />
      <Stack
        spacing={ROW_SPACING}
        sx={{
          pl: t =>
            `calc(${FEATURE_ICON_SIZE}px + ${t.spacing(FEATURE_ICON_GAP)})`,
        }}
      >
        {visibleRows.map(row => (
          <Fragment key={row.key}>{block.renderRow(row)}</Fragment>
        ))}
        {hasHiddenChanges ? (
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="secondary"
              data-cy="document-history-show-all"
              // Design: height 36, 8px vertical / 16px horizontal padding.
              sx={{ height: 36, px: 2, py: 1 }}
              onClick={() => setIsExpanded(expanded => !expanded)}
            >
              {isExpanded
                ? intl.formatMessage({
                    id: 'features.documentHistory.showLess',
                  })
                : intl.formatMessage(
                    { id: 'features.documentHistory.showAll' },
                    { count: totalChanges }
                  )}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};
